import React, { useEffect, useState } from 'react';
import { CircleAlert } from 'lucide-react';
import { portalDs } from 'src/components/portal-design-system';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { cn } from 'src/lib/utils';
import { Company } from 'src/pages/billing/types/billing.types';
import { useUpdateAccountMutation } from '../endpoints/accounts.endpoints';
import { Account } from '../types/client-management.types';
import CompanySelector from './company-selector';

interface AccountTableProps {
  accounts: Account[];
  companies: Company[];
  className?: string;
  isRefreshing?: boolean;
  onCompanySelect?: (accountId: string, companyId: string) => void;
}

const AccountTable: React.FC<AccountTableProps> = ({
  className,
  accounts,
  companies,
  isRefreshing,
  onCompanySelect,
}) => {
  const [updateAccount, { isLoading: isUpdating }] = useUpdateAccountMutation();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(Boolean(isRefreshing || isUpdating));
  }, [isRefreshing, isUpdating]);

  const handleCompanySelect = async(accountId: string, companyId: string) => {
    try {
      await updateAccount({
        accountId,
        company: companyId || null,
      }).unwrap();
      onCompanySelect?.(accountId, companyId);
    } catch (error) {
      console.error('Failed to update company:', error);
    }
  };

  return (
    <div className={cn(portalDs.tableContainer, className)}>
      <Table className={cn('w-full', isLoading && 'pointer-events-none opacity-50')}>
        <TableHeader className={portalDs.tableHead}>
          <TableRow className="border-0 hover:bg-transparent">
            <TableHead className={cn(portalDs.tableHeadCell, 'w-12')}>
              {' '}
            </TableHead>
            <TableHead className={portalDs.tableHeadCell}>
              Title
            </TableHead>
            <TableHead className={portalDs.tableHeadCell}>
              Source
            </TableHead>
            <TableHead className={cn(portalDs.tableHeadCell, 'min-w-[200px]')}>
              Company
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {accounts.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={4}
                className={cn(portalDs.tableCell, 'py-10 text-center', portalDs.tableCellMuted)}
              >
                No accounts match the current filters.
              </TableCell>
            </TableRow>
          ) : (
            accounts.map((account: Account) => (
              <TableRow
                key={account.id}
                className={cn(portalDs.tableBodyRow, 'align-top')}
              >
                <TableCell className={portalDs.tableCell}>
                  {account.attention_needed ? (
                    <span
                      className="inline-flex text-[#DC2626]"
                      title="Attention needed"
                    >
                      <CircleAlert className="size-4" />
                    </span>
                  ) : null}
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, 'font-medium')}>
                  {account.title}
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                  {(account.source ?? '')
                    .toString()
                    .toLowerCase()
                    .replace('_', ' ') || '—'}
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, 'py-2')}>
                  <CompanySelector
                    accountId={account.id}
                    companies={companies}
                    companyId={account.company || ''}
                    accountName={account.title}
                    campaignName={account.ext_account_id}
                    attentionNeeded={account.attention_needed}
                    onCompanySelect={(companyId) => handleCompanySelect(account.id, companyId)}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default AccountTable;
